/**
 * ページ詳細コンポーネント
 *
 * 保存したページの詳細情報を表示
 * - 機能B: AI要約の音声読み上げ（Web Speech API）
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPage, updatePage, deletePage } from '../../services/pages';
import { TAG_OPTIONS } from '../../services/gemini';
import { useSpeech } from '../../hooks/useSpeech';

function PageDetail() {
  const { pageId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedTags, setEditedTags] = useState([]);

  // 音声読み上げフック
  const { isSupported: speechSupported, speak, isSpeakingId } = useSpeech();

  // ページデータを読み込む
  useEffect(() => {
    if (user && pageId) {
      loadPage();
    }
  }, [user, pageId]);

  /**
   * ページデータを読み込む
   */
  async function loadPage() {
    setLoading(true);
    try {
      const pageData = await getPage(pageId);

      // 権限チェック
      if (!pageData || pageData.userId !== user.uid) {
        navigate('/pages');
        return;
      }

      setPage(pageData);
      setEditedTags(pageData.manualTags || []);
    } catch (error) {
      console.error('ページ読み込みエラー:', error);
      navigate('/pages');
    } finally {
      setLoading(false);
    }
  }

  /**
   * タグを更新
   */
  async function handleSaveTags() {
    try {
      await updatePage(pageId, { manualTags: editedTags });
      setPage(prev => ({ ...prev, manualTags: editedTags }));
      setEditing(false);
    } catch (error) {
      console.error('タグ更新エラー:', error);
      alert('タグの保存に失敗しました');
    }
  }

  /**
   * ページを削除
   */
  async function handleDelete() {
    if (!confirm('このページを削除しますか？')) {
      return;
    }

    try {
      await deletePage(pageId);
      navigate('/pages');
    } catch (error) {
      console.error('ページ削除エラー:', error);
      alert('削除に失敗しました');
    }
  }

  /**
   * タグの追加/削除を切り替え
   */
  function toggleTag(tag) {
    setEditedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  }

  // 日付をフォーマット
  const formatDate = (timestamp) => {
    if (!timestamp) return '不明';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // ドメインを抽出
  const getDomain = (url) => {
    try {
      return new URL(url).hostname;
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="card p-8 text-center">
        <div className="spinner mx-auto mb-4" />
        <p className="text-gray-500">読み込み中...</p>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="card p-8 text-center">
        <div className="text-4xl mb-4">❌</div>
        <h3 className="font-bold text-gray-900 mb-2">ページが見つかりません</h3>
        <Link to="/pages" className="text-primary hover:underline">
          ページ一覧に戻る
        </Link>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* ナビゲーション */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/pages" className="hover:text-primary">保存ページ</Link>
        <span>/</span>
        <span className="truncate">{page.title}</span>
      </div>

      {/* メインカード */}
      <div className="card">
        {/* ヘッダー */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-gray-900">
              {page.title || 'タイトルなし'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {getDomain(page.url)}
            </p>
          </div>

          {/* アクションボタン */}
          <div className="flex gap-2">
            <a
              href={page.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary px-4 py-2 text-sm"
            >
              元のページを開く
            </a>
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              削除
            </button>
          </div>
        </div>

        {/* AI要約 */}
        <section className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-gray-500">AI要約</h2>
            {/* 音声読み上げボタン */}
            {speechSupported && page.summary && (
              <button
                onClick={() => speak(page.summary, `summary-${pageId}`)}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-colors ${
                  isSpeakingId(`summary-${pageId}`)
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title={isSpeakingId(`summary-${pageId}`) ? '停止' : '読み上げ'}
              >
                {isSpeakingId(`summary-${pageId}`) ? (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </svg>
                    停止
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                    読み上げ
                  </>
                )}
              </button>
            )}
          </div>
          <p className="text-gray-800 bg-gray-50 p-4 rounded-xl">
            {page.summary || 'AI要約を生成中...'}
          </p>
        </section>

        {/* タグ */}
        <section className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-gray-500">タグ</h2>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="text-xs text-primary hover:underline"
              >
                編集
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditedTags(page.manualTags || []);
                    setEditing(false);
                  }}
                  className="text-xs text-gray-500 hover:underline"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleSaveTags}
                  className="text-xs text-primary hover:underline"
                >
                  保存
                </button>
              </div>
            )}
          </div>

          {/* 自動タグ */}
          <div className="flex flex-wrap gap-2 mb-2">
            {page.autoTags && page.autoTags.map((tag, index) => (
              <span key={index} className="tag tag-primary">
                🤖 {tag}
              </span>
            ))}
          </div>

          {/* 手動タグ（編集モード） */}
          {editing ? (
            <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl">
              {TAG_OPTIONS.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`tag ${editedTags.includes(tag) ? 'bg-primary text-white' : ''}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          ) : page.manualTags && page.manualTags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {page.manualTags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
        </section>

        {/* コンテキスト情報 */}
        <section className="mb-6">
          <h2 className="text-sm font-bold text-gray-500 mb-2">保存時の情報</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-xl">
            <div>
              <p className="text-xs text-gray-500">保存日時</p>
              <p className="text-sm font-medium text-gray-800">
                {formatDate(page.createdAt)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">曜日</p>
              <p className="text-sm font-medium text-gray-800">
                {page.context?.dayOfWeek || '不明'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">時間帯</p>
              <p className="text-sm font-medium text-gray-800">
                {page.context?.timeOfDay || '不明'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">デバイス</p>
              <p className="text-sm font-medium text-gray-800">
                {page.context?.device || '不明'}
              </p>
            </div>
          </div>
        </section>

        {/* コンテンツプレビュー */}
        {page.contentText && (
          <section>
            <h2 className="text-sm font-bold text-gray-500 mb-2">コンテンツプレビュー</h2>
            <div className="bg-gray-50 p-4 rounded-xl max-h-64 overflow-y-auto">
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {page.contentText.substring(0, 1000)}
                {page.contentText.length > 1000 && '...'}
              </p>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default PageDetail;
